/** @typedef {'global' | 'country'} TickerScope */

/**
 * @typedef {Object} TickerSection
 * @property {string} id
 * @property {TickerScope} scope
 * @property {string} label
 * @property {string} [countryCode]
 * @property {string} [platform]
 * @property {Array<Object>} items
 */

const COUNTRY_LABELS = {
  PT: 'Portugal',
  US: 'United States',
  GB: 'United Kingdom',
  ES: 'Spain',
  FR: 'France',
  DE: 'Germany',
  BR: 'Brazil',
};

export function countryLabel(code) {
  if (!code) return 'Local';
  return COUNTRY_LABELS[String(code).toUpperCase()] || String(code).toUpperCase();
}

function isTwitchItem(item) {
  const platform = String(item?.platform || item?.source || '').toLowerCase();
  return platform === 'twitch';
}

function isKickItem(item) {
  const platform = String(item?.platform || item?.source || '').toLowerCase();
  return platform === 'kick';
}

function sanitizeSection(section) {
  const items = Array.isArray(section?.items) ? section.items.filter(Boolean) : [];
  if (!items.length) return null;

  const scope = section.scope === 'country' ? 'country' : 'global';
  const countryCode = section.countryCode
    ? String(section.countryCode).toUpperCase()
    : undefined;

  return {
    id: section.id || `${scope}-${countryCode || 'world'}`,
    scope,
    label: section.label || (scope === 'global' ? 'Worldwide' : countryLabel(countryCode)),
    countryCode,
    platform: section.platform || undefined,
    items,
  };
}

export function sanitizeSections(sections) {
  if (!Array.isArray(sections)) return [];
  return sections.map(sanitizeSection).filter(Boolean);
}

function isYouTubeItem(item) {
  const platform = String(item?.platform || item?.source || '').toLowerCase();
  return platform === 'youtube';
}

function pushScopedItem(buckets, item, defaultCountryCode) {
  const scope = item.scope === 'country' ? 'country' : 'global';
  if (scope === 'country') {
    const code = String(item.countryCode || defaultCountryCode).toUpperCase();
    if (!buckets.countryByCode.has(code)) buckets.countryByCode.set(code, []);
    buckets.countryByCode.get(code).push(item);
  } else {
    buckets.global.push(item);
  }
}

/**
 * Split a legacy flat `items` array into scoped sections when items carry
 * `scope` / `countryCode` (future backend) or a single fallback section.
 */
export function partitionFlatItems(items) {
  if (!Array.isArray(items) || !items.length) return [];

  const globalTwitch = [];
  const countryTwitch = new Map();
  const globalKick = [];
  const countryKick = new Map();
  const globalYouTube = [];
  const countryYouTube = new Map();
  const other = [];

  for (const item of items) {
    if (isTwitchItem(item)) {
      pushScopedItem({ global: globalTwitch, countryByCode: countryTwitch }, item, 'PT');
      continue;
    }

    if (isKickItem(item)) {
      pushScopedItem({ global: globalKick, countryByCode: countryKick }, item, 'PT');
      continue;
    }

    if (isYouTubeItem(item)) {
      pushScopedItem({ global: globalYouTube, countryByCode: countryYouTube }, item, 'PT');
      continue;
    }

    other.push(item);
  }

  const sections = [];

  if (globalTwitch.length) {
    sections.push({
      id: 'twitch-global',
      scope: 'global',
      label: 'Worldwide',
      platform: 'twitch',
      items: globalTwitch,
    });
  }

  for (const [code, twitchItems] of countryTwitch) {
    sections.push({
      id: `twitch-${code.toLowerCase()}`,
      scope: 'country',
      countryCode: code,
      label: countryLabel(code),
      platform: 'twitch',
      items: twitchItems,
    });
  }

  if (globalKick.length) {
    sections.push({
      id: 'kick-global',
      scope: 'global',
      label: 'Worldwide',
      platform: 'kick',
      items: globalKick,
    });
  }

  for (const [code, kickItems] of countryKick) {
    sections.push({
      id: `kick-${code.toLowerCase()}`,
      scope: 'country',
      countryCode: code,
      label: countryLabel(code),
      platform: 'kick',
      items: kickItems,
    });
  }

  if (globalYouTube.length) {
    sections.push({
      id: 'youtube-global',
      scope: 'global',
      label: 'Worldwide',
      platform: 'youtube',
      items: globalYouTube,
    });
  }

  for (const [code, youtubeItems] of countryYouTube) {
    sections.push({
      id: `youtube-${code.toLowerCase()}`,
      scope: 'country',
      countryCode: code,
      label: countryLabel(code),
      platform: 'youtube',
      items: youtubeItems,
    });
  }

  if (other.length) {
    const code = String(other.find(i => i.countryCode)?.countryCode || 'PT').toUpperCase();
    sections.push({
      id: `local-${code.toLowerCase()}`,
      scope: 'country',
      countryCode: code,
      label: `${countryLabel(code)} · TV & more`,
      items: other,
    });
  }

  if (!sections.length) {
    sections.push({
      id: 'live',
      scope: 'global',
      label: 'Live now',
      items,
    });
  }

  return sanitizeSections(sections);
}

/**
 * Normalize API payload into ordered ticker sections.
 * Supports:
 * - `sections[]` (preferred future shape)
 * - `twitch.global` + `twitch.country` buckets
 * - `kick.global` + `kick.country` buckets
 * - flat `items[]` with optional per-item `scope` / `countryCode`
 */
export function normalizeHappeningNowResponse(data) {
  if (!data || typeof data !== 'object') return [];

  if (Array.isArray(data.sections) && data.sections.length) {
    return sanitizeSections(data.sections);
  }

  const twitch = data.twitch;
  const kick = data.kick;
  if (
    (twitch && typeof twitch === 'object')
    || (kick && typeof kick === 'object')
  ) {
    const sections = [];

    if (Array.isArray(twitch?.global?.items) && twitch.global.items.length) {
      sections.push({
        id: 'twitch-global',
        scope: 'global',
        label: twitch.global.label || 'Worldwide',
        platform: 'twitch',
        items: twitch.global.items,
      });
    }

    if (Array.isArray(twitch?.country?.items) && twitch.country.items.length) {
      const code = String(twitch.country.countryCode || 'PT').toUpperCase();
      sections.push({
        id: `twitch-${code.toLowerCase()}`,
        scope: 'country',
        countryCode: code,
        label: twitch.country.label || countryLabel(code),
        platform: 'twitch',
        items: twitch.country.items,
      });
    }

    if (Array.isArray(kick?.global?.items) && kick.global.items.length) {
      sections.push({
        id: 'kick-global',
        scope: 'global',
        label: kick.global.label || 'Worldwide',
        platform: 'kick',
        items: kick.global.items,
      });
    }

    if (Array.isArray(kick?.country?.items) && kick.country.items.length) {
      const code = String(kick.country.countryCode || 'PT').toUpperCase();
      sections.push({
        id: `kick-${code.toLowerCase()}`,
        scope: 'country',
        countryCode: code,
        label: kick.country.label || countryLabel(code),
        platform: 'kick',
        items: kick.country.items,
      });
    }

    const merged = sanitizeSections(sections);
    if (merged.length) return merged;
  }

  return partitionFlatItems(Array.isArray(data.items) ? data.items : []);
}

/**
 * Flatten sections into scroll track segments (section marker + items).
 * Caps items per section so the ticker matches "Top 10" labels and scroll speed stays sane.
 */
const TICKER_ITEMS_PER_SECTION = 10;

export function buildTickerTrackSegments(sections) {
  const segments = [];

  for (const section of sections) {
    segments.push({ type: 'section', key: `section-${section.id}`, section });
    const items = section.items.slice(0, TICKER_ITEMS_PER_SECTION);
    for (let i = 0; i < items.length; i += 1) {
      const item = items[i];
      segments.push({
        type: 'item',
        key: `item-${section.id}-${i}-${item.url || item.name}`,
        item,
        section,
      });
    }
  }

  return segments;
}

export function hasTickerContent(sections) {
  return sections.some(section => section.items?.length > 0);
}
